// Turn the backend's platform Q&A answer into a renderable, GROUNDED proposal. Otto's brain (the backend)
// produces the ANSWER text plus raw entry keys (citations) and deep-link targets; the frontend turns those
// into something the card can render: citations get human-friendly labels, and each deep_link is GROUNDED
// against the live, permission-scoped nav catalog so a "jump there" link only ever points at a page the
// user can actually open (best-effort — an unmatched link is dropped, never a dead button). A refusal
// (answered=false) carries nothing to cite or link. Pure/deterministic, mirroring buildNavProposal's
// (interpretResponse, ctx) parameter shape (the orchestrator dispatches both through handlerForKind).
import { groundDestination } from "../dashboardNavigation/navCatalog";
import { shortPageLabel } from "../planner/pageManifest";
import { buildActionSuggestions } from "./qaSuggestions";
import { resolveRoute, sourcesByKey, targetForSource } from "./qaTargets";

// Human-friendly fallback name for an entry key: take the LAST dotted segment, turn _/- into spaces, Title
// Case. e.g. "action.table_view.export" -> "Export", "filters.criticality_band" -> "Criticality Band".
export const prettifyKey = key => {
    const seg = String(key == null ? "" : key).split(".").pop() || "";
    return seg
        .replace(/[_-]+/g, " ")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
};

// A citation key may name a nav target (use its label) — otherwise prettify the raw key.
const labelForCitation = (key, navTargets) => {
    const hit = (navTargets || []).find(t => t && (t.key === key || t.url === key));
    return hit ? hit.label : prettifyKey(key);
};

// GROUND one deep_link (a nav URL or key) against the catalog -> the nav-target object onNavigate accepts,
// or null when nothing reachable matches (dropped). Exact url/key match first, then the same match against
// the link with its project placeholder filled in (KB deep links are router PATTERNS —
// "/project/projectinfo/:projectId/recommendations" — while catalog targets carry the concrete id, so a
// literal comparison never matched and left this leaning on fuzzy phrase grounding), then navCatalog's
// phrase grounding as the last fallback (auto only — a single confident destination).
const groundDeepLink = (deepLink, navTargets, projectId) => {
    const targets = navTargets || [];
    const exact = targets.find(t => t && (t.url === deepLink || t.key === deepLink));
    if (exact) return exact;
    const resolved = resolveRoute(deepLink, { projectId });
    if (resolved) {
        const byUrl = targets.find(t => t && t.url === resolved);
        if (byUrl) return byUrl;
    }
    const grounded = groundDestination(deepLink, targets);
    if (grounded.decision === "auto" && grounded.target) return grounded.target;
    return null;
};

// interpretResponse = { request_id, session_id, intent_kind: "platform_qa", qa: QaAnswer }; ctx = { navTargets, utterance, ... }.
// QaAnswer = { answered, answer, citations: string[], deep_links: string[], assistant_message }. The proposal's
// `qa` block is the exact shape the render card consumes (message.proposal.qa).
export const buildQaProposal = (interpretResponse, ctx = {}) => {
    const base = {
        requestId: interpretResponse && interpretResponse.request_id,
        sessionId: interpretResponse && interpretResponse.session_id,
        utterance: ctx.utterance
    };
    const navTargets = ctx.navTargets || [];
    const qa = (interpretResponse && interpretResponse.qa) || {};
    const answered = !!qa.answered;
    const answer = qa.answer || "";
    const assistantMessage = qa.assistant_message || "";

    // A refusal has nothing to cite or link to — still carry the assistant's message.
    if (!answered) {
        return { ...base, qa: { answered: false, answer, assistantMessage, citations: [], deepLinks: [] } };
    }

    // Citations carry a TARGET whenever the entry they name is a page Otto can open (see qaTargets.js) — a
    // cited page is the one thing a reader immediately wants to act on, so the source chip becomes the way
    // there instead of a dead label. The backend supplies each cited entry's metadata (`qa_sources`,
    // deterministic — not model-authored); an older backend sends none and every chip stays inert, exactly
    // as before.
    const sources = sourcesByKey(interpretResponse && interpretResponse.qa_sources);
    const projectId = ctx.projectId != null ? ctx.projectId : (ctx.projectIds || []).length === 1 ? ctx.projectIds[0] : undefined;
    const citations = (qa.citations || []).map(key => {
        const label = labelForCitation(key, navTargets);
        const target = targetForSource(sources[key], { navTargets, projectId, label });
        return target ? { key, label, target } : { key, label };
    });

    // Best-effort grounding: keep only deep_links that resolve to a reachable page; drop the rest.
    const deepLinks = (qa.deep_links || [])
        .map(deepLink => {
            const target = groundDeepLink(deepLink, navTargets, projectId);
            // Name the "Take me there" link the way Otto names the page — a data-grid reads "Recommendations Table View".
            return target ? { label: shortPageLabel(target), target } : null;
        })
        .filter(Boolean);

    // Phase 0 (Route A): turn the answer's cited, TYPED knowledge entries into a few grounded "you can also…"
    // ACTION chips — filter by a cited concept, run a cited action on a capability mounted HERE, or open a
    // cited page. Deterministic + grounded against the current page's mounted capabilities (appContext.pages)
    // and the reachable nav catalog; clicking re-enters the normal pipeline (no auto-execute). See qaSuggestions.js.
    const suggestions = buildActionSuggestions({
        citations,
        page: ctx.page,
        pages: (ctx.appContext && ctx.appContext.pages) || [],
        deepLinks,
        // FTO-11: the live field maps + browsability predicate let a cited concept offer the honest chip
        // ("Browse Category options" / "Set a Cost threshold") that lands on the FTO-10/12 cards.
        maps: ctx.maps,
        isBrowseable: ctx.isBrowseable
    });

    return { ...base, qa: { answered: true, answer, assistantMessage, citations, deepLinks, suggestions } };
};
